/** @jsxImportSource preact */
import { useRef, useState } from "preact/hooks";

import * as copy from "../paraglide/messages.js";
import { formVars } from "./form/form-styles";
import { CloseIcon } from "./icons";
import { SR_ONLY } from "./sr-only";
import type { WidgetTokens } from "./theme";

interface MessageImageProps {
  url: string;
  alt: string;
  tokens: WidgetTokens;
}

/**
 * 放大层的遮罩：和抽屉遮罩同一个近黑，压得住任何店面和面板底色。
 * 它是压在图片后面的幕布，不属于卡片皮肤，深浅主题下都得是深色，所以不走 --gn-form-* 变量。
 */
const VIEWER_SCRIM = "rgb(28 27 26 / 88%)";

/**
 * 消息图片的样式。图片是一块柔阴影的内圆角图块，放大层里的关闭键是白色圆形胶囊。
 * 变量在外层 .gn-msg-image 上；放大层是它的 DOM 后代，进了 top layer 变量也照样继承。
 * dialog 上别写 display：关着的时候靠浏览器自带的 display:none 藏起来，写了就关不上。
 */
export const MESSAGE_IMAGE_STYLES = `
.gn-msg-image { display:block; max-width:100%; min-width:0; }
.gn-msg-image-open { display:block; max-width:100%; padding:0; border:0; border-radius:var(--gn-form-r-well); background:transparent; box-shadow:var(--gn-form-shadow-pill); cursor:zoom-in; overflow:hidden; }
.gn-msg-image-open:focus-visible { outline:2px solid var(--gn-form-ring); outline-offset:2px; }
.gn-msg-image-thumb { display:block; max-width:100%; height:auto; border-radius:var(--gn-form-r-well); }
.gn-msg-image-failed { box-sizing:border-box; display:inline-block; max-width:100%; padding:8px 12px; border-radius:var(--gn-form-r-cell); background:var(--gn-form-subtle); color:var(--gn-form-quiet); font-size:12px; line-height:1.4; }
.gn-msg-image-viewer { position:fixed; inset:0; box-sizing:border-box; width:100%; height:100%; max-width:none; max-height:none; margin:0; padding:0; border:0; background:${VIEWER_SCRIM}; }
.gn-msg-image-stage { box-sizing:border-box; display:flex; align-items:center; justify-content:center; width:100%; height:100%; padding:56px 16px 16px; }
.gn-msg-image-full { display:block; max-width:100%; max-height:100%; object-fit:contain; border-radius:var(--gn-form-r-well); }
.gn-msg-image-close { position:absolute; top:12px; right:12px; box-sizing:border-box; display:flex; align-items:center; justify-content:center; width:36px; height:36px; padding:0; border:0; border-radius:var(--gn-form-r-pill); background:var(--gn-form-raised, var(--gn-form-bg)); color:var(--gn-form-ink); box-shadow:0 0 0 1px var(--gn-form-line), var(--gn-form-shadow-pill); cursor:pointer; }
.gn-msg-image-close:hover { filter:brightness(.96); }
.gn-msg-image-close:focus-visible { outline:2px solid var(--gn-form-ring); outline-offset:2px; box-shadow:0 0 0 2px var(--gn-form-raised, var(--gn-form-bg)); }
`;

/**
 * AI 消息里的图片（PRD 2.4）：点了放大，可关闭。
 *
 * 放大层用原生 modal `<dialog>`：进 top layer，不受面板的 transform 和消息列表的
 * overflow:clip 限制；焦点关在里面，其余部分不可交互。它仍是消息的 DOM 后代，
 * 所以停在主题 modal 里的挂件（见 modal-parking）和抽屉的「点外面关闭」都认它是自己人。
 * Esc 自己接：原生的 cancel 冒泡到 document，抽屉会把它当成关抽屉。
 * 图片加载失败只替换当前图片，不改变相邻块和消息状态。
 */
export function MessageImage({ url, alt, tokens: t }: MessageImageProps) {
  const [failed, setFailed] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const viewer = useRef<HTMLDialogElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const label = alt || copy.widget_message_image();

  const close = () => {
    viewer.current?.close();
  };

  return (
    <div class="gn-msg-image" style={formVars(t)}>
      <style>{MESSAGE_IMAGE_STYLES}</style>
      {failed ? (
        <span class="gn-msg-image-failed" role="status">
          {copy.widget_message_image_unavailable()}
        </span>
      ) : (
        <>
          <button
            ref={trigger}
            class="gn-msg-image-open"
            type="button"
            aria-haspopup="dialog"
            onClick={() => {
              const dialog = viewer.current;
              if (!dialog || dialog.open) return;
              dialog.showModal();
              // 原生会按 autofocus 把焦点放到关闭键；这里再补一次，不依赖各家实现。
              closeButton.current?.focus();
            }}
          >
            <img
              class="gn-msg-image-thumb"
              src={url}
              alt={label}
              loading="lazy"
              referrerPolicy="no-referrer"
              onError={() => {
                setFailed(true);
              }}
            />
            <span style={SR_ONLY}>{copy.widget_message_image_open()}</span>
          </button>
          <dialog
            ref={viewer}
            class="gn-msg-image-viewer"
            aria-label={label}
            onClose={() => {
              trigger.current?.focus();
            }}
            onKeyDown={(event) => {
              if (event.key !== "Escape") return;
              event.preventDefault();
              event.stopPropagation();
              close();
            }}
          >
            <div
              class="gn-msg-image-stage"
              onClick={(event) => {
                // 点图片以外的遮罩也关：和买家对放大层的预期一致。
                if (event.target === event.currentTarget) close();
              }}
            >
              {/* 关着的 dialog 是 display:none，lazy 的图不会提前去拉。 */}
              <img
                class="gn-msg-image-full"
                src={url}
                alt={label}
                loading="lazy"
                referrerPolicy="no-referrer"
              />
              <button
                ref={closeButton}
                class="gn-msg-image-close"
                type="button"
                autoFocus
                aria-label={copy.widget_message_image_close()}
                onClick={close}
              >
                <CloseIcon size={18} />
              </button>
            </div>
          </dialog>
        </>
      )}
    </div>
  );
}
